import Star from '@mui/icons-material/Star';
import StarBorder from '@mui/icons-material/StarBorder';
import { Checkbox, Stack, Typography, useTheme } from '@mui/material';
import { getFullName } from '@provision/formatting-utils';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_TableOptions,
  useMaterialReactTable,
} from 'material-react-table';
import { MouseEvent, useMemo } from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import { EllipsesMenu, EllipsesMenuOption } from 'src/components/EllipsesMenu/ellipses-menu';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import { useViewRecordFolders, VIEW_ASSIGNMENT_ENTITIES } from 'src/graphql';
import { CustomView } from 'src/graphql/entities/custom-view';
import {
  useCreateRecordFolderAssignment,
  useDeleteRecordFolderAssignment,
} from 'src/graphql/entities/record-folder-assignment';
import { useGlobalStore } from 'src/store';
import { useConfirmationModal } from 'src/utils/hooks';

const defaultColumn = {
  enableSorting: false,
  enableColumnActions: false,
  minSize: 20,
  maxSize: 20,
  size: 20,
};

const tableBackgroundStylingProps = {
  elevation: 0,
  sx: {
    height: '100%',
  },
};

export const FavoriteCheckbox = ({
  checked,
  onClick,
}: {
  checked: boolean;
  onClick: (e: MouseEvent<HTMLElement>) => void;
}) => (
  <Tooltip title="Toggle favorite">
    <Stack alignItems="flex-start">
      <Checkbox
        icon={<StarBorder />}
        checkedIcon={<Star />}
        checked={checked}
        onClick={onClick}
        sx={{
          padding: '0.2rem',

          color: (theme) => theme.palette.custom.OV.sys.outline,
          '&.Mui-checked': {
            color: '#F6A609',
          },
        }}
      />
    </Stack>
  </Tooltip>
);

export const CustomViewsList = ({
  rows,
  onDelete,
  onUpdate,
  onFavorite,
  onCopy,
  onClick,
}: {
  rows: CustomView[];
  onClick?: (id: string) => void;
  onDelete?: (id: string) => void;
  onUpdate?: (id: string) => void;
  onCopy?: (item: CustomView) => void;
  onFavorite?: (id: string, value: boolean) => void;
}) => {
  const theme = useTheme();
  const { confirm } = useConfirmationModal();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { manageableRecordFolders: recordFolders } = useViewRecordFolders();
  const { createRecordFolderAssignment } = useCreateRecordFolderAssignment({
    entityFilter: VIEW_ASSIGNMENT_ENTITIES,
  });
  const { deleteRecordFolderAssignment } = useDeleteRecordFolderAssignment();

  const columns = useMemo<MRT_ColumnDef<CustomView>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: true,
        Cell: ({ renderedCellValue, row }) => (
          <Stack direction="row" spacing={2}>
            <Tooltip title="Custom view icon">
              <DynamicIcon
                name={row.original.icon}
                library="lucide"
                sx={{
                  color: (theme) => theme.palette.custom.OV.sys.outline,
                }}
              />
            </Tooltip>
            <Typography
              variant="body2"
              sx={{
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
              }}
            >
              {renderedCellValue}
            </Typography>
          </Stack>
        ),
        grow: true,
        size: 400,
        minSize: 200,
        muiTableHeadCellProps: {
          sx: {
            paddingLeft: '2rem',
          },
        },
        muiTableBodyCellProps: {
          sx: {
            paddingLeft: '2rem',
          },
        },
      },
      {
        accessorKey: 'isPrivate',
        header: 'Is Private',
        grow: false,
        size: 150,
        minSize: 100,
        maxSize: 200,
        Cell: ({ renderedCellValue }) =>
          renderedCellValue && (
            <Tooltip title="Private view, visible to you only" enterDelay={700}>
              <DynamicIcon
                sx={{
                  color: (theme) => theme.palette.custom.OV.sys.outline,
                }}
                name="visibility_off"
              />
            </Tooltip>
          ),
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
      },
      {
        accessorKey: 'isFavorite',
        header: 'Favorite',
        grow: false,
        size: 150,
        minSize: 100,
        maxSize: 200,
        Cell: ({ row }) => (
          <Stack alignItems="flex-start">
            <FavoriteCheckbox
              checked={row.original.isFavorite}
              onClick={(e: MouseEvent<HTMLElement>) => {
                onFavorite?.(row.original.customViewId, !row.original.isFavorite);
                e.stopPropagation();
              }}
            />
          </Stack>
        ),
        muiTableHeadCellProps: {
          align: 'center',
        },
        muiTableBodyCellProps: {
          align: 'center',
        },
      },
      {
        accessorFn: (row: CustomView) => {
          if (row.partnerId === PLATFORM_PARTNER_ID) {
            return 'Platform';
          }

          return getFullName(row?.createdByUser ?? {});
        },
        header: 'Created By',
        grow: true,
        size: 300,
        minSize: 200,
      },
    ],
    [onFavorite],
  );

  const config = useMemo<MRT_TableOptions<CustomView>>(
    () => ({
      columns,
      enableRowVirtualization: true,
      enableColumnVirtualization: true,
      data: rows,
      defaultColumn,
      enableStickyHeader: true,
      enableTopToolbar: false,
      enableBottomToolbar: false,
      enableRowActions: true,
      positionActionsColumn: 'last',
      enablePagination: false,
      muiTablePaperProps: tableBackgroundStylingProps,
      muiTableContainerProps: {
        sx: {
          height: '100%',
        },
      },
      muiTableBodyRowProps: ({ row }) => {
        const id = row.original?.customViewId;

        return {
          onClick: () => {
            onClick?.(id);
          },
          sx: {
            cursor: onClick && 'pointer',
          },
        };
      },
      initialState: {
        columnPinning: {
          right: ['mrt-row-actions'],
        },
        sorting: [
          {
            id: 'name',
            desc: false,
          },
        ],
      },
      renderRowActions: ({ row }) => {
        const shouldAllowToEdit = selectedPartnerId === row.original.partnerId;

        const options: EllipsesMenuOption[] = [];

        if (onUpdate && shouldAllowToEdit) {
          options.push({
            label: 'Update',
            onClick: () => onUpdate(row.original.customViewId),
          });
        }

        if (onDelete && shouldAllowToEdit) {
          options.push({
            label: 'Delete',
            onClick: async () => {
              if (
                await confirm({
                  body: 'Please confirm you want to delete the View.\nThis process cannot be undone.',
                  variant: 'delete',
                })
              ) {
                onDelete(row.original.customViewId);
              }
            },
          });
        }

        options.push({
          label: 'Copy',
          onClick: () => onCopy?.(row.original),
        });

        if (recordFolders?.length) {
          options.push({
            label: 'Add to folder...',
            subItems: recordFolders.map((folder) => {
              const assignment = folder.assignments?.find(
                (i) => i.entityId === row.original.customViewId,
              );

              return {
                id: folder.id,
                label: folder.name,
                icon: 'folder',
                iconColor: assignment ? theme.palette.custom.OV.sys.secondary : undefined,
                selected: Boolean(assignment),
                onClick: () => {
                  if (assignment) {
                    void deleteRecordFolderAssignment(assignment);
                  } else {
                    void createRecordFolderAssignment({
                      recordFolderId: folder.id,
                      entity: 'CustomView',
                      entityId: row.original.customViewId,
                      partnerId: selectedPartnerId ?? PLATFORM_PARTNER_ID,
                      sortOrder: 0,
                      title: row.original.name,
                    });
                  }
                },
              };
            }),
          });
        }
        return <EllipsesMenu options={options} />;
      },
    }),
    [
      columns,
      rows,
      onClick,
      selectedPartnerId,
      onUpdate,
      onDelete,
      recordFolders,
      confirm,
      onCopy,
      theme.palette.custom.OV.sys.secondary,
      deleteRecordFolderAssignment,
      createRecordFolderAssignment,
    ],
  );

  const table = useMaterialReactTable(config);

  return <MaterialReactTable table={table} />;
};
